/* eslint-disable vue/one-component-per-file */
import LeafletMap from "@/components/mapping/LeafletMap"
import LocationMarker from "@/components/mapping/LocationMarker"
import { useMapDistanceMeasuring } from "@/composition/useMapDistanceMeasuring"
import type { IncidentLocation } from "@/models/IncidentLocation"
import { LatLngFormatType, fixLatLng, formatLatLng } from "@/utils/formatting/formatLatLng"
import type { Map } from "leaflet"
import { css } from "vite-css-in-js"
import { toRef, watch } from "vue"
import { defineComponent, inferProps, renderSlot, type ReactiveComponent } from "vue-utils"
import { useIncidentHandler } from "../../IncidentLogHandler"
import { generateMarkerFilter } from "./generateMarkerFilter"

interface Props {
	locations: IncidentLocation[]
}

const mapMeasureStyles = css`
	cursor: crosshair;

	.leaflet-interactive,
	.leaflet-map-pane,
	.leaflet-control-container,
	.leaflet-grab {
		cursor: crosshair;
	}
	.leaflet-drag-target {
		cursor: grab;
	}
`

const AllLocationsMap: ReactiveComponent<Props> = (props, { slots }) => {
	const handler = useIncidentHandler()

	const { MeasureDistance, initMeasuring, measureData } = useMapDistanceMeasuring()

	let map: Map
	const markers: LocationMarker[] = []

	function initMap(mapData: Map, container: HTMLElement) {
		map = mapData

		initMeasuring(mapData)

		container.style.borderRadius = "0.5rem"
		container.style.overflow = "hidden"

		updateMarkers()
	}

	function updateMarkers() {
		if (!map) {
			return
		}
		for (const marker of markers) {
			marker.removeFrom(map)
		}
		markers.length = 0

		for (let i = 0; i < props.locations.length; i++) {
			const loc = props.locations[i]
			/*
			 	Leaflet only supports binding markers directly to HTML, rather than VueJS
				Use Vue's in built "render" function to render a Vue component version of the tooltip directly to a container div
			 */
			const tooltipDiv = document.createElement("div")

			const locationIndex = props.locations.indexOf(loc)
			const title = document.createElement("span")

			title.innerText = i === 0 ? "Initial Incident Location" : `Additional Incident Location ${locationIndex}`
			title.style.fontFamily = "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
			title.style.fontWeight = "500"

			const fixedLoc = fixLatLng(loc.position)

			const horizRule = document.createElement("hr")
			horizRule.className = "my-1"

			tooltipDiv.append(title)
			tooltipDiv.append(horizRule)
			tooltipDiv.append(new Text(`Received at: ${handler.formatTime(loc.receivedAt)}`))
			tooltipDiv.append(document.createElement("br"))
			tooltipDiv.append(new Text(formatLatLng(fixedLoc, LatLngFormatType.DegreesDecimalMinutes)))

			const marker = new LocationMarker({
				latitude: loc.position.latitude,
				longitude: loc.position.longitude,
				tooltipHtml: tooltipDiv.innerHTML,
			})

			if (loc.id !== 0) {
				marker.setFilter(generateMarkerFilter(locationIndex))
			}

			markers.push(marker)
			marker.addTo(map)
		}
	}

	watch(toRef(props, "locations"), updateMarkers)

	return () => (
		<LeafletMap
			initMap={initMap}
			mapOptions={{
				zoomControl: true,
			}}
			zoomControlPosition="bottomright"
			class={["flex-grow-1", measureData.value.isMeasuring ? mapMeasureStyles : undefined]}
		>
			<MeasureDistance />
			{renderSlot(slots)}
		</LeafletMap>
	)
}

export default defineComponent(AllLocationsMap, inferProps<Props>())
